import React from 'react';
import { Tag, Tooltip } from 'antd';
import {
  ClockCircleOutlined,
  ExclamationCircleOutlined,
  WarningOutlined,
} from '@ant-design/icons';
import { daysUntil, parseDateLocal } from '@/utils/dateFormatters';

interface DeadlineCountdownProps {
  deadline: string; // ISO date string (e.g. "2026-06-15")
  label?: string;
  compact?: boolean; // compact mode for pipeline cards
  showTooltip?: boolean; // default true; set false when the date is already visible alongside
}

const DeadlineCountdown: React.FC<DeadlineCountdownProps> = ({
  deadline,
  label,
  compact,
  showTooltip = true,
}) => {
  const daysRemaining = daysUntil(deadline);

  let color: string;
  let icon: React.ReactNode;
  let displayText: string;

  if (daysRemaining < 0) {
    color = '#DC2626';
    icon = <ExclamationCircleOutlined />;
    displayText = `${Math.abs(daysRemaining)}d overdue`;
  } else if (daysRemaining <= 1) {
    color = '#DC2626';
    icon = <WarningOutlined />;
    displayText = daysRemaining === 0 ? 'Due today' : '1d left';
  } else if (daysRemaining <= 3) {
    color = '#F59E0B';
    icon = <WarningOutlined />;
    displayText = `${daysRemaining}d left`;
  } else if (daysRemaining <= 7) {
    color = '#F59E0B';
    icon = <ClockCircleOutlined />;
    displayText = `${daysRemaining}d left`;
  } else {
    color = '#10B981';
    icon = <ClockCircleOutlined />;
    displayText = `${daysRemaining}d left`;
  }

  const fullDate = parseDateLocal(deadline).format('MMM D, YYYY');
  const tooltipText = `${label || 'Phase Deadline'}: ${fullDate}`;

  const compactNode = (
    <span
      style={{
        display: 'inline-flex',
        alignItems: 'center',
        gap: 3,
        fontSize: 10,
        color,
        fontWeight: daysRemaining <= 3 ? 600 : 400,
      }}
    >
      {icon}
      {displayText}
    </span>
  );

  const tagNode = (
    <Tag
      style={{
        color,
        borderColor: color,
        background: `${color}10`,
        fontWeight: daysRemaining <= 3 ? 600 : 400,
      }}
      icon={icon}
    >
      {label ? `${label}: ` : ''}{displayText}
    </Tag>
  );

  const node = compact ? compactNode : tagNode;
  return showTooltip ? <Tooltip title={tooltipText}>{node}</Tooltip> : node;
};

export default DeadlineCountdown;
